import gql from 'graphql-tag';
import { API } from 'aws-amplify';

const GET_KPI_VALUES_QUERY = `
  query GetKPIValuesV6(
    $dimensionType: String!
    $dimensionTimeframe: String!
    $dimensionValue: String!
    $nextToken: String
    $limit: Int
    $sortColumn: String
    $sortOrder: String
  ) {
    getKPIValuesV6(
      dimensionType: $dimensionType
      dimensionTimeframe: $dimensionTimeframe
      dimensionValue: $dimensionValue
      nextToken: $nextToken
      limit: $limit
      sortColumn: $sortColumn
      sortOrder: $sortOrder
    )
  }
`;

/**
 * Parse the legacy dimension string (e.g. "D:2024-01-15", "M:2024-01", "current:current")
 * into the { dimensionTimeframe, dimensionValue } format the V6 lambda expects.
 */
function parseDimension(dimension) {
  if (!dimension) return null;

  // "current:current" means today's stages
  if (dimension === 'current:current') {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return { dimensionTimeframe: 'D', dimensionValue: `${yyyy}-${mm}-${dd}` };
  }

  // "A:" means all time — use Y with a very early year to capture everything
  if (dimension.startsWith('A:')) {
    const bestSuffix = dimension.split(':')[1];
    if (bestSuffix === 'BEST') {
      // All-time personal best — not supported by V6 lambda
      return null;
    }
    // All time: use a range from org creation to now
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    return {
      dimensionTimeframe: 'R',
      dimensionValue: `2020-01-01:${yyyy}-${mm}-${dd}`,
    };
  }

  const colonIndex = dimension.indexOf(':');
  if (colonIndex === -1) return null;

  const timeframe = dimension.substring(0, colonIndex);
  const value = dimension.substring(colonIndex + 1);

  // BEST variants (D:BEST, W:BEST, etc.) are not supported by V6 lambda
  if (value === 'BEST') return null;

  // P: pay period — map to W (weekly) as closest equivalent
  if (timeframe === 'P') {
    return { dimensionTimeframe: 'W', dimensionValue: value };
  }

  // D, W, M, Y map directly
  if (['D', 'W', 'M', 'Y'].includes(timeframe)) {
    return { dimensionTimeframe: timeframe, dimensionValue: value };
  }

  return null;
}

/**
 * Strip formatting characters ($, %, commas) from the lambda's formatted value strings
 * and return a numeric value.
 */
function parseFormattedValue(formattedVal) {
  if (
    formattedVal === null ||
    formattedVal === undefined ||
    formattedVal === ''
  )
    return 0;
  const cleaned = String(formattedVal).replace(/[$,%]/g, '').trim();
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

const fetchStats = async (userId, dimension, kpiRules) => {
  let stagesOnly = false;
  if (dimension.endsWith(':current:current')) {
    stagesOnly = true;
  }

  const parsed = parseDimension(dimension);

  // If we can't parse the dimension (e.g. BEST variants), return empty
  if (!parsed) {
    console.warn('fetchStats: unsupported dimension for V6 lambda:', dimension);
    return [];
  }

  const { dimensionTimeframe, dimensionValue } = parsed;

  // Fetch preferences for KPI ordering
  const getPreferenceQuery = gql`
    query MyQuery($id: ID = "") {
      getPreferences(id: $id) {
        preferences
      }
    }
  `;

  let ourPreferences = [];
  const userPreferenceID =
    global.me.orgID + ':KPIBoard:' + 'user' + ':' + global.me.id;
  let preferenceRes = await API.graphql({
    query: getPreferenceQuery,
    variables: { id: userPreferenceID },
  });

  if (preferenceRes.data.getPreferences === null) {
    const orgPreferenceID =
      global.me.orgID + ':KPIBoard:' + 'org' + ':' + global.me.orgID;
    preferenceRes = await API.graphql({
      query: getPreferenceQuery,
      variables: { id: orgPreferenceID },
    });
  }

  if (preferenceRes.data.getPreferences != null) {
    ourPreferences = JSON.parse(preferenceRes.data.getPreferences.preferences);
  }

  try {
    // Call the V6 lambda for a single user's stats
    // nextToken=userId + limit=1 + sortColumn="id" returns just this user's row
    const res = await API.graphql({
      query: GET_KPI_VALUES_QUERY,
      variables: {
        dimensionType: 'person',
        dimensionTimeframe,
        dimensionValue,
        nextToken: userId,
        limit: 1,
        sortColumn: 'id',
        sortOrder: 'desc',
      },
    });

    const parsedResult = JSON.parse(res.data.getKPIValuesV6);
    const { results, kpiList } = parsedResult;
    // kpiList: { abbreviation: title }
    // results: [{ id, name, kpis: { abbreviation: "formattedValue" }, rank, imageName }]

    // Build a reverse map: abbreviation -> kpiRule (from the useListKPIs hook data)
    const abbreviationToRule = {};
    for (const rule of kpiRules) {
      if (rule.abbreviation) {
        abbreviationToRule[rule.abbreviation] = rule;
      }
    }

    // Get the user's row from results (should be exactly 1 or 0)
    const userRow = results.find((r) => r.id === userId);
    const userKpis = userRow ? userRow.kpis : {};

    // Build KPIValues in the shape the UserStats component expects
    let KPIValues = [];

    for (const [abbreviation, title] of Object.entries(kpiList)) {
      const rule = abbreviationToRule[abbreviation];
      if (!rule) continue;

      // Apply stagesOnly filter
      if (
        stagesOnly &&
        rule.sourceType !== 'stage' &&
        rule.sourceType !== 'manual'
      ) {
        continue;
      }

      const formattedVal = userKpis[abbreviation];
      const numericVal = parseFormattedValue(formattedVal);

      // Build the KPIRule shape that UserStats expects
      const kpiEntry = {
        val: numericVal,
        isHidden: false,
        KPIRule: {
          id: rule.id,
          title: rule.title || title,
          sourceType: rule.sourceType,
          action: rule.action,
          valueUnitPlural: rule.valueUnitPlural,
          isDeleted: rule.isDeleted,
          triggerID: rule.triggerID,
          format: rule.format,
          entitlement: rule.entitlement,
          scorecardViewerRoles: rule.scorecardViewerRoles,
          order: rule.order,
        },
      };

      KPIValues.push(kpiEntry);
    }

    // Add KPIs from rules that weren't in the lambda response (show as 0)
    const returnedAbbreviations = new Set(Object.keys(kpiList));
    for (const rule of kpiRules) {
      if (rule.isDeleted || rule.isHidden) continue;
      if (rule.abbreviation && returnedAbbreviations.has(rule.abbreviation))
        continue;
      if (
        stagesOnly &&
        rule.sourceType !== 'stage' &&
        rule.sourceType !== 'manual'
      )
        continue;

      KPIValues.push({
        val: 0,
        isHidden: false,
        KPIRule: {
          id: rule.id,
          title: rule.title,
          sourceType: rule.sourceType,
          action: rule.action,
          valueUnitPlural: rule.valueUnitPlural,
          isDeleted: rule.isDeleted,
          triggerID: rule.triggerID,
          format: rule.format,
          entitlement: rule.entitlement,
          scorecardViewerRoles: rule.scorecardViewerRoles,
          order: rule.order,
        },
      });
    }

    // Apply visibility rules based on scorecardViewerRoles
    for (const kpi of KPIValues) {
      const KPI = kpi.KPIRule;
      let canViewKPI = false;

      if (
        global.me.isAdmin ||
        KPI.scorecardViewerRoles === null ||
        !KPI.scorecardViewerRoles ||
        KPI.scorecardViewerRoles.includes('everyone')
      ) {
        canViewKPI = true;
      } else if (
        KPI.scorecardViewerRoles.includes('moderator') &&
        global.me.isModerator
      ) {
        canViewKPI = true;
      } else if (
        KPI.scorecardViewerRoles.includes('owner') &&
        userId === global.me.id
      ) {
        canViewKPI = true;
      }

      if (!canViewKPI) {
        kpi.isHidden = true;
      }
    }

    // Sort KPIs based on preferences or order
    let sortedKpis;
    if (ourPreferences.length === 0) {
      let hasOrderValue = false;
      for (const KPI of KPIValues) {
        if (KPI.KPIRule.order !== null && KPI.KPIRule.order !== undefined) {
          hasOrderValue = true;
        } else {
          KPI.KPIRule.order = 99999;
        }
      }
      if (hasOrderValue) {
        sortedKpis = KPIValues.sort((a, b) => {
          if (a.KPIRule.order < b.KPIRule.order) return -1;
          if (a.KPIRule.order > b.KPIRule.order) return 1;
          return 0;
        });
      } else {
        sortedKpis = KPIValues.sort((a, b) => {
          if (a.KPIRule.title.toLowerCase() < b.KPIRule.title.toLowerCase())
            return -1;
          if (a.KPIRule.title.toLowerCase() > b.KPIRule.title.toLowerCase())
            return 1;
          return 0;
        });
      }
    } else {
      const ourNewKPIList = [];
      for (const kpi of KPIValues) {
        const isRuleID = (element) => element.id === kpi.KPIRule.id;
        const index = ourPreferences.findIndex(isRuleID);
        if (index >= 0) {
          kpi.index = index;
          kpi.isHidden = ourPreferences[index].isHidden || kpi.isHidden;
        } else {
          kpi.index = 1000;
        }
        if (kpi.isHidden) {
          kpi.index = 9999;
        }
        ourNewKPIList.push(kpi);
      }
      KPIValues = ourNewKPIList;
      sortedKpis = KPIValues.sort((a, b) => a.index - b.index);
    }

    return sortedKpis;
  } catch (err) {
    console.error('fetchStats error: ', err);
    return [];
  }
};

export default fetchStats;
